import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

const Header = () => {

  const { t } = useTranslation();

  // stagger motion animation
  const containerMotion = {
    visible: { transition: { staggerChildren: 0.2 } },
  };

  // animation parameters for TEXT
  const textMotion = {
    // movement = FADE-IN + UPWARDS movement
    hidden: { opacity: 0, x: -50 }, // INITIAL STAGE
    visible: { opacity: 1, x: 0, transition: { duration: 0.5, ease: 'easeInOut' } }, // ANIMATION STAGE
  };

  // animation parameters for IMAGE
  const colMotion_UP = {
    // movement = FADE-IN + SLIDE UP
    hidden: { opacity: 0, y: 100 }, // INITIAL STAGE
    visible: { opacity: 1, y: 0, transition: { duration: 1.25, ease: 'easeInOut' } }, // ANIMATION STAGE
  };

  // animation parameters for IMAGE
  const colMotion_DOWN = {
    // movement = FADE-IN + SLIDE DOWN
    hidden: { opacity: 0, y: -100 }, // INITIAL STAGE
    visible: { opacity: 1, y: 0, transition: { duration: 1.25, ease: 'easeInOut' } }, // ANIMATION STAGE
  };

  return (
    <div className="relative overflow-hidden bg-white">
      {/* INITIAL PAGE SETUPS -> TAILWINDCSS */}
      <div className="pb-80 pt-12 sm:pb-40 sm:pt-24 lg:pb-48 lg:pt-40">
        <div className="relative mx-auto max-w-7xl px-4 sm:static sm:px-6 lg:px-8">

          {/* HEADING TEXT */}
          <motion.div className="sm:max-w-lg"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.2 }}
            variants={containerMotion}>
            <motion.h1 className="text-5xl font-bold tracking-tight text-[#291A1A] sm:text-6xl" variants={textMotion}>
              {t("Indulge in the")}
            </motion.h1>
            <motion.h1 className="text-5xl font-bold tracking-tight text-[#EA6A5E] sm:text-6xl" variants={textMotion}>
              {t("extraordinary")}
            </motion.h1>
            <motion.h1 className="text-3xl font-bold tracking-tight text-[#291A1A] sm:text-4xl mt-2" variants={textMotion}>
              {t("A New Dimension to Style.")}
            </motion.h1>
            {/* SUBHEADING TEXT */}
            <motion.p className="mt-4 text-xm text-[#291A1A]" variants={textMotion}>
              {t("Inspired by the cosmic wonders, we curated a collection that blends elegance, innovation, and a touch of magic. Here, its one small step for Zelox, one celestial leap for fashion.")}
            </motion.p>
          </motion.div>

          <motion.div className="mt-10"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.2 }}
            variants={containerMotion}
          >
            {/* HEADER IMAGES */}
            <div
              aria-hidden="true"
              className="lg:absolute lg:inset-y-0 lg:mx-auto lg:w-full lg:max-w-7xl"
            >
              <div className="absolute transform sm:left-1/2 sm:top-0 sm:translate-x-8 lg:left-1/2 lg:top-1/2 lg:-translate-y-1/2 lg:translate-x-8">
                <div className="flex items-center space-x-6 lg:space-x-8">
                  {/* IMAGE COLUMN #1 */}
                  <motion.div className="grid flex-shrink-0 grid-cols-1 gap-y-6 lg:gap-y-8" variants={colMotion_DOWN}>
                    <Link to={'/men/clothing/tops/nike6'}>
                      <div className="h-64 w-44 overflow-hidden rounded-lg sm:opacity-0 lg:opacity-100 shadow-xl">
                        <img
                          src="https://storage.googleapis.com/esenca-static-hosting/demo-shop/products/men/16/product_men_8-3.jpg"
                          alt="model-1-F"
                          className="h-full w-full object-cover object-center"
                        />
                      </div>
                    </Link>
                    <Link to={'/men/clothing/tops/nike1'}>
                      <div className="h-64 w-44 overflow-hidden rounded-lg shadow-xl">
                        <img
                          src="https://storage.googleapis.com/esenca-static-hosting/demo-shop/products/men/1/product_men_1-2.jpg"
                          alt="model-2-M"
                          className="h-full w-full object-cover object-center"
                        />
                      </div>
                    </Link>
                  </motion.div>

                  {/* IMAGE COLUMN #2 */}
                  <motion.div className="grid flex-shrink-0 grid-cols-1 gap-y-6 lg:gap-y-8" variants={colMotion_UP}>
                    <Link to={'/men/clothing/bottoms/zara2'}>
                      <div className="h-64 w-44 overflow-hidden rounded-lg shadow-xl">
                        <img
                          src="https://storage.googleapis.com/esenca-static-hosting/demo-shop/products/men/14/product_men_6-2.jpg"
                          alt="model-3-F"
                          className="h-full w-full object-cover object-center"
                        />
                      </div>
                    </Link>
                    <Link to={'/men/clothing/bottoms/zara1'}>
                      <div className="h-64 w-44 overflow-hidden rounded-lg shadow-xl">
                        <img
                          src="https://storage.googleapis.com/esenca-static-hosting/demo-shop/products/men/2/product_men_2-3.jpg"
                          alt="model-4-M"
                          className="h-full w-full object-cover object-center shadow-xl"
                        />
                      </div>
                    </Link>
                    <Link to={'/women/clothing/tops/nike4'}>
                      <div className="h-64 w-44 overflow-hidden rounded-lg shadow-xl">
                        <img
                          src="https://storage.googleapis.com/esenca-static-hosting/demo-shop/products/women/4/product_women_4-3.jpg"
                          alt="model-5-F"
                          className="h-full w-full object-cover object-center"
                        />
                      </div>
                    </Link>
                  </motion.div>

                  {/* IMAGE COLUMN #3 */}
                  <motion.div className="grid flex-shrink-0 grid-cols-1 gap-y-6 lg:gap-y-8" variants={colMotion_DOWN}>
                    <Link to={'/women/clothing/bottoms/zara1'}>
                      <div className="h-64 w-44 overflow-hidden rounded-lg shadow-xl">
                        <img
                          src="https://storage.googleapis.com/esenca-static-hosting/demo-shop/products/women/2/product_women_2-2.jpg"
                          alt="model-6-F"
                          className="h-full w-full object-cover object-center"
                        />
                      </div>
                    </Link>
                    <Link to={'/women/clothing/tops/nike3'}>
                      <div className="h-64 w-44 overflow-hidden rounded-lg shadow-xl">
                        <img
                          src="https://storage.googleapis.com/esenca-static-hosting/demo-shop/products/women/12/product_women_7-2.jpg"
                          alt="model-7-M"
                          className="h-full w-full object-cover object-center"
                        />
                      </div>
                    </Link>
                  </motion.div>
                </div>
              </div>

            </div>

            {/* HEADER BUTTON */}
            <motion.span variants={textMotion}>
              <Link to="/men/clothing/" className="inline-block rounded-[10px] border border-transparent bg-[#291A1A] px-4 lg:px-8 py-2 lg:py-3 text-center font-medium text-white transition-transform duration-300 transform hover:scale-95 hover:bg-[#EA6A5E] shadow-lg">
                {t("Explore Collection")}
              </Link>
            </motion.span>
          </motion.div>

        </div>
      </div>
    </div>
  )
}

export default Header